import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp } from 'lucide-react';
import './Profile.css';

const SectionIcon = ({ Icon, color = '#0891b2', bg = '#ecfeff' }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      borderRadius: 10,
      background: bg,
      color,
      marginRight: 10,
      verticalAlign: 'middle',
    }}
  >
    <Icon size={18} strokeWidth={2} />
  </span>
);

const Profile = ({ user: initialUser, onUserUpdated }) => {
  const navigate = useNavigate();
  // `initialUser` llega como prop desde App.js (ya lo pidió a /users/me al
  // arrancar; PrivateRoute solo renderiza Profile cuando ya está cargado).
  // Se copia a estado local porque aquí se edita antes de guardar, pero ya
  // no hace falta volver a pedirlo con un GET — antes era una consulta a
  // Neo4j duplicada e idéntica en cada visita a esta página.
  const [user, setUser] = useState(initialUser);
  const [oppositions, setOppositions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error', msg }
  const [progress, setProgress] = useState(null);
  const [statsVs, setStatsVs] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const oppositionsRes = await axios.get('/oppositions');
        setOppositions(oppositionsRes.data);
      } catch (error) {
        console.error('Error fetching data', error);
        // Fallback: oposiciones por defecto si el endpoint no existe
        setOppositions(['Auxilio', 'Tramitación Procesal']);
      }
      // Progreso + comparativa
      try {
        const [p, s] = await Promise.all([
          axios.get('/api/me/progress'),
          axios.get('/api/me/stats-vs-average').catch(() => ({ data: null })),
        ]);
        setProgress(p.data);
        setStatsVs(s.data);
      } catch (e) { /* silent */ }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox' && name === 'interested_oppositions') {
      const current = user.interested_oppositions || [];
      const newOps = current.includes(value)
        ? current.filter((op) => op !== value)
        : [...current, value];
      setUser({ ...user, interested_oppositions: newOps });
    } else {
      setUser({ ...user, [name]: type === 'checkbox' ? checked : value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      const { data } = await axios.put('/users/me', user);
      // App.js solo pide /users/me una vez al arrancar; sin esto, Home
      // seguía viendo el usuario viejo (sin las oposiciones recien
      // guardadas) porque navigate('/') no vuelve a montar App.
      if (onUserUpdated) onUserUpdated(data);
      setFeedback({ type: 'success', msg: '¡Perfil actualizado! Redirigiendo…' });
      setTimeout(() => navigate('/'), 700);
    } catch (error) {
      console.error('Error updating profile', error);
      setFeedback({ type: 'error', msg: 'Error al actualizar el perfil.' });
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return <div className="page-loader">Cargando…</div>;
  }

  return (
    <div className="profile-page">
      <header className="page-header">
        <h1 className="page-title">Mi perfil</h1>
        <p className="page-subtitle">Gestiona tu información y oposiciones de interés</p>
      </header>

      {progress && (
        <div className="card profile-card progress-card">
          <h3 className="section-title" style={{ display: 'flex', alignItems: 'center' }}>
            <SectionIcon Icon={TrendingUp} />
            Tu progreso
          </h3>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">Artículos leídos</div>
              <div className="stat-value">{progress.articles_read}</div>
              {statsVs && statsVs.avg_reads > 0 && (
                <div className="stat-foot">Media: {statsVs.avg_reads}</div>
              )}
            </div>
            <div className="stat-card">
              <div className="stat-label">Preguntas contestadas</div>
              <div className="stat-value">{progress.questions_answered}</div>
              {statsVs && statsVs.avg_answered > 0 && (
                <div className="stat-foot">Media: {statsVs.avg_answered}</div>
              )}
            </div>
            <div className="stat-card">
              <div className="stat-label">Aciertos</div>
              <div className="stat-value">{progress.accuracy_pct ?? '—'}{progress.accuracy_pct !== null ? '%' : ''}</div>
              {statsVs && statsVs.avg_accuracy > 0 && (
                <div className="stat-foot">Media: {statsVs.avg_accuracy}%</div>
              )}
            </div>
          </div>
          {progress.coverage && progress.coverage.length > 0 && (
            <div className="coverage-section">
              <div className="stat-label">Cobertura del temario</div>
              {progress.coverage.map((c) => (
                <div key={c.oposicion} className="coverage-row">
                  <div className="coverage-label">
                    <strong>{c.oposicion}</strong> · {c.leidos} / {c.total} artículos ({c.pct}%)
                  </div>
                  <div className="coverage-track">
                    <div className="coverage-fill" style={{ width: `${c.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="card profile-card">
        {feedback && (
          <div className={`alert alert-${feedback.type}`}>{feedback.msg}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nombre</label>
            <input
              type="text"
              name="name"
              value={user.name || ''}
              onChange={handleChange}
              placeholder="Tu nombre"
            />
          </div>

          <div className="form-group">
            <label>Correo electrónico</label>
            <input type="email" name="email" value={user.email} disabled />
          </div>

          <div className="form-group">
            <label>Ciudad</label>
            <input
              type="text"
              name="city"
              value={user.city || ''}
              onChange={handleChange}
              placeholder="Madrid, Barcelona…"
            />
          </div>

          <div className="form-group">
            <label>Oposiciones de interés</label>
            <div className="checkbox-list">
              {oppositions.map((op) => (
                <label key={op} className="checkbox-row">
                  <input
                    type="checkbox"
                    name="interested_oppositions"
                    value={op}
                    checked={(user.interested_oppositions || []).includes(op)}
                    onChange={handleChange}
                  />
                  <span>{op}</span>
                </label>
              ))}
            </div>
          </div>

          <label className="checkbox-row toggle-row">
            <input
              type="checkbox"
              name="receive_notifications"
              checked={!!user.receive_notifications}
              onChange={handleChange}
            />
            <span>Recibir notificaciones sobre convocatorias y exámenes</span>
          </label>

          <div className="profile-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? 'Guardando…' : 'Guardar perfil'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile;
